<script setup>
import { ref, onMounted, computed, onUnmounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import axios from '@/plugins/axios';
import { API_BASE_URL } from '@/constants';
import eventBus from '@/eventBus';
import SearchResultsPanel from './SearchResultsPanel.vue';
import { useUserStore } from '@/stores/user';
import { useDataStore } from '@/stores/data';
import { useCreditUsage } from '@/composables/useCreditUsage';
import QueueStatus from './QueueStatus.vue';
import LanguageSelector from './LanguageSelector.vue';
import { UsageBarsSkeleton } from '@/design-system/components/molecules';
import { BRANDING } from '@/branding';

const { t } = useTranslation();

const props = defineProps({
    offsetTop: {
        type: Number,
        default: 0
    }
});

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const dataStore = useDataStore();

const showEditIcon = ref(false);
const searchQuery = ref('');
const filteredResults = ref([]);
const showResultsPanel = ref(false);
const searchFocused = ref(false);
const searchInputRef = ref(null);
const searchResultsPanelRef = ref(null);
const highlightedIndex = ref(-1);

const userName = computed(() => userStore.name);
const userEmail = computed(() => userStore.email);
const userAvatarSrc = computed(() => userStore.userAvatarSrc);
const useDefaultAvatar = computed(() => !userStore.userAvatarSrc);
const defaultAvatar = ref('mdi-account-circle');

// v2.0 Subscription Data
const aggregatedUsage = computed(() => userStore.aggregatedUsage);
const isLoadingUsage = computed(() => userStore.isLoadingUsage);
const usageError = computed(() => userStore.usageError);

// Brain Simulations: cliente unified vê 1 medidor único; legado segue nos 2 medidores.
const { isUnified: isUnifiedBilling, meters: creditMeters } = useCreditUsage();
const brainSimulationsMeter = computed(() => (isUnifiedBilling.value ? creditMeters.value[0] : null));

const isAdmin = computed(() => userStore.isAdmin);
const isSuperAdmin = computed(() => userStore.isSuperAdmin);
// SYN-1694 — mesmo gate da sidebar: superadmin OU staff com alguma área.
const canAccessAdminPanel = computed(() => userStore.canAccessAdminPanel);

const filterResults = () => {
    highlightedIndex.value = -1;
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) {
        filteredResults.value = [];
        showResultsPanel.value = false;
        return;
    }

    const flattenedItems = [];
    // Request 3: Recursively build path during traversal
    const traverse = (nodes, currentPath) => {
        if (!nodes) return;
        for (const node of nodes) {
            if (node.id && typeof node.id === 'string' && node.id.startsWith('intro-')) continue;
            
            // Build the path for this node
            const nodePath = currentPath ? `${currentPath}/${node.name}` : node.name;
            
            // Push node with the calculated path
            flattenedItems.push({ ...node, logical_path: nodePath });
            
            if (node.type === 'folder' && node.children) {
                traverse(node.children, nodePath);
            }
        }
    };
    
    // Start traversal with root name if available, or empty string
    traverse(dataStore.folderTree, "");

    const results = [];
    const queryRegex = new RegExp(query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'i');

    flattenedItems.forEach(item => {
        if ((item.type === 'folder' || item.type === 'experiment') && item.name && queryRegex.test(item.name.toLowerCase())) {
            results.push({
                ...item,
                type: item.type,
                logical_path: item.logical_path
            });
        }
    });

    dataStore.allTags.forEach(tag => {
        if (tag.name && queryRegex.test(tag.name.toLowerCase())) {
            results.push({ ...tag, type: 'tag' });
        }
    });

    results.sort((a, b) => {
        const typeOrder = { folder: 0, experiment: 1, tag: 2 };
        const orderA = typeOrder[a.type] ?? 99;
        const orderB = typeOrder[b.type] ?? 99;
        if (orderA !== orderB) return orderA - orderB;
        return (a.name || '').localeCompare(b.name || '', 'pt-BR');
    });

    filteredResults.value = results.slice(0, 15);
    showResultsPanel.value = searchFocused.value && filteredResults.value.length > 0;
};

watch(searchQuery, filterResults);

const onSearchFocus = () => {
    searchFocused.value = true;
    if (searchQuery.value.trim()) {
       filterResults();
    }
};

const onSearchBlur = () => {
    setTimeout(() => {
        const active = document.activeElement;
        if (!active ||
            (!active.closest?.('.search-results-panel-position') && active !== searchInputRef.value?.$el?.querySelector('input')))
        {
             searchFocused.value = false;
             showResultsPanel.value = false;
             highlightedIndex.value = -1;
        }
    }, 200);
};

const handleResultSelect = (item) => {
    if (!item) return;
    showResultsPanel.value = false;
    searchQuery.value = '';
    highlightedIndex.value = -1;
    searchInputRef.value?.blur();

    if (item.type === 'folder') {
        router.push(`/main/${item.id}`);
    } else if (item.type === 'experiment') {
        let fromPath = '/main';
        if (item.folder_id) {
            fromPath = `/main/${item.folder_id}`;
        }
        router.push({
            name: 'ExperimentView',
            params: { experimentId: item.id },
            query: { from: fromPath }
        });
    } else if (item.type === 'tag') {
        eventBus.emit('select-tag-from-search', item);
    }
};

const handleKeydown = async (event) => {
     if (!showResultsPanel.value || filteredResults.value.length === 0) {
        if (event.key === 'Enter' && searchQuery.value.trim()) {
            performFullSearch();
        }
        return;
    }

    const lastIndex = filteredResults.value.length - 1;

    switch (event.key) {
        case 'ArrowDown':
            event.preventDefault();
            highlightedIndex.value = (highlightedIndex.value === lastIndex) ? -1 : highlightedIndex.value + 1;
            await nextTick();
            scrollToHighlightedItem();
            break;
        case 'ArrowUp':
            event.preventDefault();
            highlightedIndex.value = (highlightedIndex.value <= 0) ? lastIndex : highlightedIndex.value - 1;
             await nextTick();
            scrollToHighlightedItem();
            break;
        case 'Enter':
            event.preventDefault();
            if (highlightedIndex.value >= 0) {
                handleResultSelect(filteredResults.value[highlightedIndex.value]);
            } else if (searchQuery.value.trim()) {
                performFullSearch();
            }
            break;
        case 'Escape':
            showResultsPanel.value = false;
            highlightedIndex.value = -1;
            searchInputRef.value?.blur();
            break;
    }
};

const scrollToHighlightedItem = () => {
    if (highlightedIndex.value < 0 || !searchResultsPanelRef.value) return;
    const panelElement = searchResultsPanelRef.value.$el;
    if (!panelElement) return;
    const highlightedItemElement = panelElement.querySelector(`.search-result-item[data-index="${highlightedIndex.value}"]`);
    if (highlightedItemElement) {
        highlightedItemElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
};

const performFullSearch = () => {
    const term = searchQuery.value.trim();
    if (!term) return;
    showResultsPanel.value = false;
    highlightedIndex.value = -1;
    searchInputRef.value?.blur();
    eventBus.emit('perform-full-search', term);
};

const logout = () => {
    userStore.logout();
};

const goToSettings = () => {
    if (route.name === 'MainWithFolder') {
        router.push({
            name: 'MainWithFolder',
            params: { folderId: route.params.folderId },
            query: { ...route.query, view: 'settings' }
        });
        return;
    }
    if (route.name === 'MainWithoutFolder') {
        router.push({ name: 'MainWithoutFolder', query: { ...route.query, view: 'settings' } });
        return;
    }
    router.push({ name: 'Settings' });
};

const goToSuperAdminPanel = () => {
    router.push({ name: 'SuperAdminPanel' });
};

const clearSearchInputHandler = () => {
    searchQuery.value = '';
    highlightedIndex.value = -1;
};

onMounted(() => {
    eventBus.on('clear-search-input', clearSearchInputHandler);
});

onUnmounted(() => {
    eventBus.off('clear-search-input', clearSearchInputHandler);
});

// --- Usage Bar Helpers ---
const getUsageColor = (used, total) => {
    if (total === 0) return 'grey';
    const percentage = (used / total) * 100;
    if (percentage >= 100) return 'error';
    if (percentage >= 80) return 'warning';
    return 'success';
};

</script>

<template>
    <div class="main-app-bar" :style="{ top: props.offsetTop + 'px' }">
        <v-container fluid class="d-flex align-center pa-0 fill-height px-4">
            <router-link to="/main" class="header-logo d-flex align-center flex-shrink-0 ml-4 mr-4">
                 <v-img src="/logo-horz.svg" alt="SynapSee Studio" height="60" width="146" contain />
            </router-link>
            <v-spacer></v-spacer>

            <div class="d-flex align-center flex-shrink-0 ml-auto">
                 <div class="search-container">
                    <v-text-field
                        ref="searchInputRef"
                        v-model="searchQuery"
                        variant="outlined"
                        density="compact"
                        :placeholder="t('components.common.header.search.placeholder')"
                        :aria-label="t('components.common.header.search.placeholder')"
                        hide-details
                        single-line
                        rounded="lg"
                        class="search-field flex-grow-0 mr-4"
                        @focus="onSearchFocus"
                        @blur="onSearchBlur"
                        @keydown="handleKeydown"
                        autocomplete="off"
                    >
                        <!-- SYN-2738 (A8): botão de busca com nome traduzido (o ícone
                             clicável do Vuetify só dizia " appended action", em inglês). -->
                        <template #append-inner>
                            <button type="button" class="search-submit" :aria-label="t('components.common.header.search.submit')" @click="performFullSearch">
                                <v-icon icon="mdi-magnify" />
                            </button>
                        </template>
                    </v-text-field>
                     <SearchResultsPanel
                        ref="searchResultsPanelRef"
                        v-if="showResultsPanel && filteredResults.length > 0"
                        :results="filteredResults"
                        :search-term="searchQuery"
                        :highlighted-index="highlightedIndex"
                        @select-item="handleResultSelect"
                        class="search-results-panel-position"
                    />
                 </div>

                <!-- Brain Simulations (billing_model=unified): single AVAILABLE meter -->
                <div v-if="brainSimulationsMeter" class="usage-container mr-4 d-none d-md-flex align-center">
                    <div class="usage-item">
                        <div class="d-flex justify-space-between ga-2 text-caption mb-1">
                            <span class="font-weight-medium header-text-muted">{{ t('components.common.header.usage.brainSimulations') }}</span> <span class="header-text-muted">{{ brainSimulationsMeter.remaining }} / {{ brainSimulationsMeter.allowance }}</span>
                        </div>
                        <v-progress-linear
                            :model-value="brainSimulationsMeter.remainingPercent"
                            :color="brainSimulationsMeter.color"
                            height="6"
                            rounded
                            style="width: 100px;"
                        ></v-progress-linear>
                    </div>
                </div>

                <!-- Subscription Usage Display (Aggregated) — shows AVAILABLE credits -->
                <div v-else-if="aggregatedUsage" class="usage-container mr-4 d-none d-md-flex align-center">
                    <!-- Image Usage -->
                    <div class="usage-item mr-4">
                        <div class="d-flex justify-space-between ga-2 text-caption mb-1">
                            <!-- SYN-2738 (E2E): espaço real entre rótulo e número (sem ele, "Imagens298 / 300") -->
                            <span class="font-weight-medium header-text-muted">{{ t('components.common.header.usage.images') }}</span> <span class="header-text-muted">{{ aggregatedUsage.total_images_allowance - aggregatedUsage.total_images_used }} / {{ aggregatedUsage.total_images_allowance }}</span>
                        </div>
                        <v-progress-linear
                            :model-value="((aggregatedUsage.total_images_allowance - aggregatedUsage.total_images_used) / aggregatedUsage.total_images_allowance) * 100"
                            :color="getUsageColor(aggregatedUsage.total_images_used, aggregatedUsage.total_images_allowance)"
                            height="6"
                            rounded
                            style="width: 100px;"
                        ></v-progress-linear>
                    </div>

                    <!-- Video Usage -->
                    <div class="usage-item">
                        <div class="d-flex justify-space-between ga-2 text-caption mb-1">
                            <span class="font-weight-medium header-text-muted">{{ t('components.common.header.usage.videos') }}</span> <span class="header-text-muted">{{ aggregatedUsage.total_videos_allowance - aggregatedUsage.total_videos_used }} / {{ aggregatedUsage.total_videos_allowance }}</span>
                        </div>
                        <v-progress-linear
                            :model-value="((aggregatedUsage.total_videos_allowance - aggregatedUsage.total_videos_used) / aggregatedUsage.total_videos_allowance) * 100"
                            :color="getUsageColor(aggregatedUsage.total_videos_used, aggregatedUsage.total_videos_allowance)"
                            height="6"
                            rounded
                            style="width: 100px;"
                        ></v-progress-linear>
                    </div>
                </div>
                
                <!-- Error state -->
                <div v-else-if="usageError" class="mr-4 text-caption text-error">
                    {{ t('components.common.header.usage.errorLoadingPlan') }}
                </div>
                <!-- Loading skeleton (default while aggregatedUsage is null and no error) -->
                <div v-else class="usage-container mr-4 d-none d-md-flex align-center" aria-busy="true">
                    <UsageBarsSkeleton />
                </div>

                <LanguageSelector class="mr-2" />

                <!-- New Queue Status Component -->
                <QueueStatus class="mr-2" />

                 <div
                    class="avatar-container mp-sensitive mx-2"
                    @mouseenter="showEditIcon = true"
                    @mouseleave="showEditIcon = false"
                 >
                    <v-avatar
                        size="40"
                        class="user-avatar"
                        @click="goToSettings"
                        :title="t('components.common.header.avatar.settingsTooltip')"
                    >
                        <v-img v-if="!useDefaultAvatar" :src="userAvatarSrc" alt="User Avatar" />
                        <span v-else-if="useDefaultAvatar && userStore.userInitials !== '?'" class="text-h6">{{ userStore.userInitials }}</span>
                        <v-icon v-else size="large">{{ defaultAvatar }}</v-icon>
                    </v-avatar>
                    <v-icon v-if="showEditIcon" class="edit-icon" size="x-small">mdi-cog</v-icon>
                 </div>

                <v-menu offset-y>
                    <template v-slot:activator="{ props: menuProps }">
                         <v-btn v-bind="menuProps" icon="mdi-menu" variant="text" class="ml-1 hamburger-menu-btn" :aria-label="t('components.common.header.menu.open')"></v-btn>
                    </template>
                    <v-list density="compact" nav>
                        <v-list-item v-if="userName" class="mp-sensitive">
                            <v-list-item-title class="font-weight-medium">{{ userName }}</v-list-item-title>
                            <v-list-item-subtitle>{{ userEmail }}</v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item v-else>
                             <v-list-item-title>{{ t('components.common.header.menu.userFallback') }}</v-list-item-title>
                        </v-list-item>
                        <v-divider class="my-1"></v-divider>

                        <!-- Unified Settings Link -->
                        <v-list-item @click="goToSettings" prepend-icon="mdi-cog-outline" :title="t('components.common.header.menu.settings')"></v-list-item>

                        <!-- Superadmin Link (Visible only to Superadmin) -->
                        <v-list-item v-if="canAccessAdminPanel" @click="goToSuperAdminPanel" prepend-icon="mdi-shield-crown-outline" :title="t('components.common.header.menu.superadmin')"></v-list-item>

                        <v-divider class="my-1"></v-divider>
                        <v-list-item @click="logout" prepend-icon="mdi-logout-variant" :title="t('components.common.header.menu.logout')"></v-list-item>
                    </v-list>
                </v-menu>
             </div>

        </v-container>
    </div>
</template>

<style scoped>
/* Style for the main div replacing v-app-bar */
.main-app-bar {
    height: 80px;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
    display: flex;
    align-items: center;
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1005;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    border-bottom: none;
    transition: top 0.2s ease-in-out;
    color: white;
}

.app-header .v-container { min-width: 0; }

/* Search Container */
.search-container {
    position: relative;
    min-width: 350px;
    max-width: 450px;
    margin-right: 16px;
    z-index: 1007; /* Ensure search is above other header elements if needed */
}

.search-field {
    width: 100%;
}
.search-field :deep(.v-field) {
    border-radius: 32px !important;
    box-shadow: none !important;
    background-color: rgba(255, 255, 255, 0.15) !important;
    border: 1px solid rgba(255, 255, 255, 0.25) !important;
    height: 40px;
    color: white !important;
}
.search-field :deep(.v-field--focused) {
    background-color: rgba(255, 255, 255, 0.95) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
    color: var(--color-text-primary) !important;
}
.search-field :deep(.v-field input::placeholder) {
    color: rgba(255, 255, 255, 0.7) !important;
}
.search-field :deep(.v-field--focused input::placeholder) {
    color: var(--color-text-tertiary) !important;
}
.search-field :deep(.v-field__append-inner .v-icon) {
    color: rgba(255, 255, 255, 0.7) !important;
}
.search-field :deep(.v-field--focused .v-field__append-inner .v-icon) {
    color: var(--color-text-secondary) !important;
}
.search-field :deep(.v-field__field) {
    align-items: center;
    height: 40px; /* Ensure field itself respects height */
}
.search-field :deep(.v-field__input) {
     font-size: 0.9rem;
     padding-top: 0; /* Adjust padding if needed */
     padding-bottom: 0;
}
.search-field :deep(.v-field__append-inner) {
    padding-top: 0 !important; /* Vuetify might add padding here */
    align-items: center;
    display: flex;
}


/* Results Panel Position */
.search-results-panel-position {
    position: absolute;
    top: calc(100% + 4px); /* Position below the input */
    left: 0;
    width: 100%; /* Match input width */
    z-index: 1008; /* Ensure panel is above other content */
}

/* Usage Container — white chip on red header */
.usage-container {
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    padding: 6px 14px;
    backdrop-filter: blur(4px);
}
.usage-item {
    display: flex;
    flex-direction: column;
}

/* Force all icons inside header to be white */
.main-app-bar :deep(.v-icon),
.main-app-bar .v-icon {
    color: white !important;
}

/* Queue/bell button on red header */
.main-app-bar :deep(.queue-btn),
.main-app-bar :deep(.queue-icon) {
    color: white !important;
}

/* Notification bell badge */
.main-app-bar :deep(.v-btn--variant-text) {
    color: white !important;
}

/* Avatar Container */
.avatar-container {
    position: relative;
    display: inline-block;
    cursor: pointer;
    border-radius: 50%;
    vertical-align: middle;
    flex-shrink: 0;
    width: 40px; /* Ensure container respects size */
    height: 40px; /* Ensure container respects size */
    display: flex;
    align-items: center;
    justify-content: center;
}

.edit-icon {
    position: absolute;
    bottom: -2px;
    right: -2px;
    background-color: var(--color-surface);
    border-radius: 50%;
    border: 1px solid var(--color-border-subtle);
    padding: 1px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.15);
    pointer-events: none;
    z-index: 1;
    opacity: 0;
    transform: scale(0.8);
    transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
}
.avatar-container:hover .edit-icon {
    opacity: 1;
    transform: scale(1);
}

/* Avatar image/initials styling */
.user-avatar {
     background-color: rgba(255, 255, 255, 0.2);
     color: white;
     border: 1px solid rgba(255, 255, 255, 0.3);
}
.user-avatar .v-img {
  object-fit: cover; /* Ensure image covers the avatar area */
}
.user-avatar .text-h6 {
    line-height: 1; /* Adjust line height for initials */
    font-weight: 500;
}

/* Logo Link */
.d-flex.align-center.flex-shrink-0 {
    cursor: pointer;
}

/* Logo SVG — invert to white on red header */
.header-logo :deep(img) {
    transition: filter 200ms cubic-bezier(0.4, 0, 0.2, 1);
    filter: brightness(0) invert(1);
}

/* Header text muted (for usage labels on red bg) */
.header-text-muted {
    color: rgba(255, 255, 255, 0.9) !important;
}

/* Progress bar track on red header */
.usage-container :deep(.v-progress-linear__background) {
    background-color: rgba(255, 255, 255, 0.25) !important;
}


/* Hamburger menu button */
.hamburger-menu-btn {
    color: white !important;
    flex-shrink: 0;
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    min-height: 40px !important;
}
.hamburger-menu-btn:hover {
    background-color: rgba(255, 255, 255, 0.15) !important;
}
.hamburger-menu-btn :deep(.v-btn__content) {
    color: white !important;
}

/* Ensure menu items have appropriate density */
.v-list-item {
    min-height: 40px;
}
.v-list-item-title {
    font-size: 0.9rem;
}
.v-list-item-subtitle {
    font-size: 0.8rem;
}

/* SYN-2738 (A8): botão de busca no append-inner — visual do ícone de antes. */
.search-submit {
    display: inline-flex;
    align-items: center;
    background: none;
    border: none;
    padding: 0;
    color: inherit;
    cursor: pointer;
    opacity: var(--v-medium-emphasis-opacity);
}
</style>
